/**
 * Demand card (TRUS-820, D2). Shown on the partner connect page and on the
 * outreach landing page. Every layer stays separate; numbers come from the
 * API already small-cell suppressed.
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { DemandCardData, renderCard } from './demandCardLogic';

export default function DemandCards({ cards }: { cards: DemandCardData[] }) {
  const { t } = useAppTranslation();
  if (!cards || cards.length === 0) return null;
  return (
    <section className="mt-10 space-y-4" aria-labelledby="demand-card-title">
      <h2 id="demand-card-title" className="text-sm font-semibold uppercase tracking-wider text-slate-500">
        {t('demandCard.title', 'Need near you')}
      </h2>
      {cards.map((card) => (
        <article key={card.service_type} className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
          <h3 className="font-semibold text-slate-900">{card.label || card.service_type}</h3>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            {renderCard(card, t).map((ln) => (
              <div key={ln.key} className="rounded-lg bg-slate-50 p-3">
                <dt className="text-xl font-semibold text-slate-900">{ln.figure}</dt>
                <dd className="mt-1 text-sm text-slate-600">{ln.caption}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-xs text-slate-500">
            {t('demandCard.smallCell', 'Counts under {{n}} are hidden.', { n: card.small_cell })}{' '}
            {card.weekly_cap
              ? t('demandCard.capSet', 'We send up to your weekly cap ({{n}} a week).', { n: card.weekly_cap })
              : t('demandCard.cap', 'We send up to your weekly cap.')}
          </p>
          {card.sources.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {card.sources.map((s) => (
                <li key={`${s.name}-${s.url ?? ''}`}>
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-700 underline">
                      {s.name}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-slate-600">{s.name}</span>
                  )}
                  {s.as_of && <span className="text-slate-400"> · {t('demandCard.asOf', 'as of {{date}}', { date: s.as_of })}</span>}
                </li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </section>
  );
}
